import React, { ReactNode, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useService } from 'react-service-locator';
import { debounce } from 'lodash';
import styled from 'styled-components';
import { Icon } from 'src/components/general/icon';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { TextInput } from 'src/components/general/inputs/text-input';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { BroadcastWizardStepProps } from 'src/components/pages/broadcast/components/broadcast-wizard/broadcast-wizard';
import {
  WizardStepMessage,
  WizardStepSectionTitle,
} from 'src/components/pages/broadcast/components/broadcast-wizard/shared';
import { useCompanySmsChannelResources } from 'src/components/pages/broadcast/hooks/use-company-sms-channel-resources';
import { BreadcrumbsService } from 'src/services/breadcrumbs.service';

const GeneralInformationContainer = styled(FlexColumn)`
  margin-bottom: 24px;
  margin-top: 20px;
  max-width: 406px;
  width: 100%;

  & + & {
    margin-bottom: 17px;
  }
`;

const GeneralInformationInputsContainer = styled.span`
  & > * {
    :not(:last-child) {
      margin-bottom: 17px;
    }
  }
`;

const StepContainer = styled(FlexColumn)`
  margin-top: 4px;
`;

const SubtitleSeparator = styled.span`
  margin-right: 4px;
  margin-left: 4px;
`;

const SubtitleText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SenderSubtitle = styled(FlexRow)`
  align-items: center;
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 11px;
  line-height: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .anticon {
    margin-right: 4px;
  }
`;

export const GeneralStep = ({ form }: BroadcastWizardStepProps) => {
  const { t } = useTranslation(['broadcast']);
  const { updateLastBreadcrumb } = useService(BreadcrumbsService, () => []);

  const debouncedBroadcastNameChangedHandler = useMemo(
    () =>
      debounce((name: string | undefined | null) => {
        updateLastBreadcrumb(name ?? 'Leadgogo');
      }, 300),
    [updateLastBreadcrumb],
  );

  useEffect(() => {
    const innerFormSubscription = form.watch(({ name }) => {
      debouncedBroadcastNameChangedHandler(name);
    });

    return () => {
      innerFormSubscription.unsubscribe();
    };
  }, [debouncedBroadcastNameChangedHandler, form]);

  const {
    resources: smsChannelResources,
    loadMore,
    loading: loadingSmsChannelResources,
    hasNextPage,
    called,
  } = useCompanySmsChannelResources({
    isActive: { _eq: true },
    tcrCampaign: { _eq: 'ACTIVE' },
  });

  const noSenderOptionsAvailable = useMemo(
    () => !loadingSmsChannelResources && called && smsChannelResources.length === 0,
    [called, loadingSmsChannelResources, smsChannelResources.length],
  );

  const senderOptions: SelectOption<string>[] = useMemo(() => {
    return smsChannelResources.map((resource) => {
      const campaign = resource.campaign?.name;
      const source = resource.source?.name;
      let subtitle: ReactNode = undefined;

      if (source && campaign) {
        subtitle = (
          <SenderSubtitle>
            <Icon type="sourceNoPadding" />
            <SubtitleText>{source}</SubtitleText> <SubtitleSeparator>·</SubtitleSeparator>
            <Icon type="campaignNoPadding" /> <SubtitleText>{campaign}</SubtitleText>
          </SenderSubtitle>
        );
      }

      return {
        value: resource.id,
        label: resource.phoneNumber?.national ?? resource.displayName,
        subTitle: subtitle,
      };
    });
  }, [smsChannelResources]);

  return (
    <StepContainer data-lgg-id="broadcast-wizard-step-general">
      <WizardStepSectionTitle>
        {t('broadcast:pages.broadcastWizard.steps.general.generalInformation')}
      </WizardStepSectionTitle>
      <GeneralInformationContainer>
        <GeneralInformationInputsContainer>
          <Controller
            control={form.control}
            name="name"
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => {
              return (
                <TextInput
                  {...field}
                  data-lgg-id="broadcast-wizard-name-input"
                  error={Boolean(fieldState.error)}
                  label={t('broadcast:pages.broadcastWizard.steps.general.broadcastName')}
                  autoComplete="off"
                  reserveErrorArea={false}
                  showErrorMessage={false}
                  value={field.value ?? ''}
                />
              );
            }}
          />
          <Controller
            control={form.control}
            name="sender.id"
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => {
              const selectedOption =
                senderOptions.find(
                  ({ value: optionValue }) => optionValue === field.value,
                ) ?? null;

              return (
                <Select
                  options={senderOptions}
                  isSearchable={false}
                  name="broadcast-wizard-sender-input"
                  isClearable={false}
                  isMulti={false}
                  forceCaret
                  label={t('broadcast:pages.broadcastWizard.steps.general.selectSender')}
                  isDisabled={noSenderOptionsAvailable}
                  canLoadMore={hasNextPage}
                  onLoadMore={loadMore}
                  isLoading={loadingSmsChannelResources}
                  hasError={Boolean(fieldState.error)}
                  onChange={(option) => {
                    if (option?.value) {
                      form.setValue('sender', {
                        id: option.value,
                        label: option.label,
                      });
                    }
                  }}
                  value={selectedOption}
                />
              );
            }}
          />
        </GeneralInformationInputsContainer>
        {noSenderOptionsAvailable && (
          <WizardStepMessage
            title={t(
              'broadcast:pages.broadcastWizard.steps.general.noSendersAvailableMessage',
            )}
            type="info"
            data-lgg-id="broadcast-wizard-no-configured-sender-message"
          />
        )}
      </GeneralInformationContainer>
    </StepContainer>
  );
};
